import dayjs from 'dayjs';
import { useWatch } from 'react-hook-form';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { ISimulationPayload } from '@schemas/scenarios-simulator/types';
import { ScenarioBasePath } from '../types';

dayjs.extend(customParseFormat);

interface IUseUnitPayloadReturn {
  years: number[];
  startYear?: number | null;
  endYear?: number | null;
  source: 'unitPayload' | 'global' | 'unknown';
  aux__useDifferentPeriodsByUnit?: any;
  supplyStartDate?: string | null;
  supplyFinalDate?: string | null;
  validity?: string | null;
  monthToSample?: string | null;
}

const parseYear = (value?: string | null): number | null => {
  if (!value) return null;
  const v = String(value).trim();

  const yearOnly = /^\d{4}$/;
  if (yearOnly.test(v)) return Number(v);

  const formats = ['YYYY-MM-DD', 'DD/MM/YYYY', 'MM/DD/YYYY', 'YYYY'];
  const parsed = dayjs(v, formats, true);
  if (parsed.isValid()) return parsed.year();

  const fallback = new Date(v);
  if (!Number.isNaN(fallback.getTime())) return fallback.getFullYear();

  return null;
};

const rangeInclusive = (start: number, end: number): number[] => {
  if (start > end) return [];
  const arr: number[] = [];
  for (let y = start; y <= end; y++) y && arr.push(y);
  return arr;
};

export const useUnitPayload = ({
  unitNum,
  scenarioBasePath,
}: {
  unitNum: number;
  unitIndex?: number;
  scenarioBasePath: ScenarioBasePath;
}): IUseUnitPayloadReturn => {
  const aux__useDifferentPeriodsByUnit = useWatch<ISimulationPayload>({
    name: 'inputs.parsedPayload.aux__useDifferentPeriodsByUnit',
  });

  const supplyStartDate = useWatch<ISimulationPayload>({
    name: `${scenarioBasePath}.scenarioPayload.${unitNum}.unitPayload.supplyStartDate`,
  }) as string;

  const supplyFinalDate = useWatch<ISimulationPayload>({
    name: `${scenarioBasePath}.scenarioPayload.${unitNum}.unitPayload.supplyFinalDate`,
  }) as string;

  const globalSupplyStartDate = useWatch<ISimulationPayload>({
    name: 'inputs.parsedPayload.aux__useGlobalSupplyStartDate',
  });
  const globalsupplyFinalDate = useWatch<ISimulationPayload>({
    name: 'inputs.parsedPayload.aux__useGlobalSupplyFinalDate',
  });
  const auxEnabled = !!aux__useDifferentPeriodsByUnit && aux__useDifferentPeriodsByUnit !== false;

  if (auxEnabled) {
    const startYear = parseYear(supplyStartDate);
    const endYear = parseYear(supplyFinalDate);
    const years = startYear && endYear ? rangeInclusive(startYear, endYear) : [];
    return {
      years,
      startYear: startYear,
      endYear: endYear,
      source: 'unitPayload',
      aux__useDifferentPeriodsByUnit,
      supplyStartDate: supplyStartDate,
      supplyFinalDate: supplyFinalDate,
    };
  }
  const globalSupplyStartYear = parseYear(globalSupplyStartDate as string);
  const globalSupplyEndYear = parseYear(globalsupplyFinalDate as string);
  if (globalSupplyStartYear && globalSupplyEndYear) {
    return {
      years: rangeInclusive(globalSupplyStartYear, globalSupplyEndYear),
      startYear: globalSupplyStartYear,
      endYear: globalSupplyEndYear,
      source: 'global',
      aux__useDifferentPeriodsByUnit,
      supplyStartDate: globalSupplyStartDate as string,
      supplyFinalDate: globalsupplyFinalDate as string,
    };
  }

  const globalYears = rangeInclusive(globalSupplyStartYear as number, globalSupplyEndYear as number);

  return {
    years: globalYears,
    startYear: globalSupplyStartYear,
    endYear: globalSupplyEndYear,
    source: 'global',
    aux__useDifferentPeriodsByUnit,
  };
};

export default useUnitPayload;
